import * as Sentry from '@sentry/react';
import { AppearanceOptions, loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import { env } from 'env';
import { useMemo, useState } from 'react';
import { useCreateStripeAccountSessionLazyQuery } from 'shared/types/graphql';
import useClinicUser from './useClinicUser';

interface StripeAccountSessionOutput {
  stripeConnectInstance?: StripeConnectInstance;
}

const connectComponentAppearance: AppearanceOptions = {
  overlays: 'drawer',
  variables: {
    colorPrimary: '#4F830F',
    colorText: '#000000',
    buttonSecondaryColorBackground: '#F5F5F5',
    buttonSecondaryColorBorder: '#F5F5F5',
    buttonSecondaryColorText: '#000000',
    colorSecondaryText: '#706E6C',
    actionPrimaryColorText: '#497CC3',
    colorDanger: '#CA4038',
    badgeNeutralColorBackground: '#EDEBEB',
    badgeNeutralColorBorder: '#EDEBEB',
    badgeNeutralColorText: '#000000',
    badgeSuccessColorBackground: '#E5F9C6',
    badgeSuccessColorBorder: '#E5F9C6',
    badgeSuccessColorText: '#3E660C',
    badgeWarningColorBackground: '#FFF6D7',
    badgeWarningColorBorder: '#FFF6D7',
    badgeWarningColorText: '#DF9F23',
    badgeDangerColorBackground: '#FFDFE6',
    badgeDangerColorBorder: '#FFDFE6',
    badgeDangerColorText: '#CA4038',
    offsetBackgroundColor: '#F5F5F5',
    borderRadius: '12px',
    buttonBorderRadius: '8px',
    formBorderRadius: '16px',
    badgeBorderRadius: '8px',
    overlayBackdropColor: '#C5C5C57A',
    spacingUnit: '8px',
  },
};

export const useStripeAccountSession = (): StripeAccountSessionOutput => {
  const { currentClinic } = useClinicUser();
  const [createAccountSession] = useCreateStripeAccountSessionLazyQuery();

  const isPaymentsEnabled = useMemo(
    () => !!currentClinic?.submerchantStripeId && !!currentClinic.submerchantStripeChargesEnabled,
    [currentClinic],
  );

  const [stripeConnectInstance] = useState(() => {
    if (!isPaymentsEnabled) {
      return undefined;
    }

    const fetchClientSecret = async (): Promise<string> => {
      const res = await createAccountSession({
        variables: {
          data: {
            stripeAccountId: currentClinic?.submerchantStripeId || '',
          },
        },
        onError: (e) => {
          Sentry.captureException(e);
        },
      });

      return res.data?.stripeCreateAccountSession?.clientSecret || '';
    };

    return loadConnectAndInitialize({
      publishableKey: env.REACT_APP_STRIPE_API_KEY,
      fetchClientSecret: fetchClientSecret,
      appearance: connectComponentAppearance,
    });
  });

  return {
    stripeConnectInstance,
  };
};
